// JSON-LD for the blog.
//
// The 126 posts and every listing page carried no structured data at all,
// while the homepage and product pages already had some. Without it Google has
// to infer what an article is, who wrote it and when - exactly the signals
// that help a "crawled, currently not indexed" page cross the line - and the
// posts are ineligible for article rich results.
import { SITE_URL, getCanonicalUrl } from '@/helpers/seo'
import { blogPagePath, blogTagPath } from '@/helpers/blog-pagination'

const ORGANISATION = {
  '@type': 'Organization',
  name: 'Pedalsure',
  url: `${SITE_URL}/`,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/logo.png`,
  },
}

// Schema.org wants an absolute URL; Ghost's feature images are already
// absolute, but a relative path would otherwise be emitted as-is.
function absoluteImage(image) {
  if (!image) {
    return undefined
  }
  return image.startsWith('http') ? image : `${SITE_URL}${image}`
}

function breadcrumbList(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: getCanonicalUrl(crumb.path),
    })),
  }
}

export function blogPostingSchema(post, canonicalUrl) {
  const author = post.authors && post.authors.length ? post.authors[0] : null

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    url: canonicalUrl,
    headline: post.meta_title || post.title,
    description: post.meta_description || post.excerpt || undefined,
    image: absoluteImage(post.feature_image),
    datePublished: post.published_at || undefined,
    // Google uses dateModified to decide whether to recrawl; falling back to
    // published_at is better than omitting it.
    dateModified: post.updated_at || post.published_at || undefined,
    author: author ? { '@type': 'Person', name: author.name } : ORGANISATION,
    publisher: ORGANISATION,
  }
}

// Home > Blog > [Topic] > Post. The topic step is only included when the post
// carries a hub tag, because that is the only case where the path it describes
// actually exists on the site.
export function postBreadcrumbSchema(post, slug) {
  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
  ]

  const hubTag = post.tags && post.tags.length ? post.tags[0] : null
  if (hubTag) {
    crumbs.push({ name: hubTag.name, path: blogTagPath(hubTag.slug) })
  }

  crumbs.push({ name: post.title, path: `/blog/${slug}` })

  return breadcrumbList(crumbs)
}

// Listing and hub pages. ItemList names the posts actually on this page, so
// page 2 of a hub describes page 2 rather than duplicating page 1.
export function collectionPageSchema({ name, description, path, posts = [] }) {
  const canonicalUrl = getCanonicalUrl(path)

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': canonicalUrl,
    url: canonicalUrl,
    name,
    description,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Pedalsure',
      url: `${SITE_URL}/`,
    },
    publisher: ORGANISATION,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: posts.length,
      itemListElement: posts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: getCanonicalUrl(`/blog/${post.slug}`),
        name: post.title,
      })),
    },
  }
}

export function listingBreadcrumbSchema(tag, page) {
  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
  ]

  if (tag) {
    crumbs.push({ name: tag.name, path: blogTagPath(tag.slug) })
  }

  if (page > 1) {
    crumbs.push({
      name: `Page ${page}`,
      path: tag ? blogTagPath(tag.slug, page) : blogPagePath(page),
    })
  }

  return breadcrumbList(crumbs)
}

// vue-meta renders `json` script entries as JSON-LD; hid keeps them deduped.
export function jsonLdScript(hid, json) {
  return { hid, type: 'application/ld+json', json }
}

// Tags Google will render inside a FAQPage acceptedAnswer.text — everything
// else gets stripped so the schema can carry hand-authored HTML answers
// (e.g. an inline <a>) without violating Google's structured data policy.
const FAQ_ANSWER_ALLOWED_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'br',
  'ol',
  'ul',
  'li',
  'a',
  'p',
  'div',
  'b',
  'strong',
  'i',
  'em',
]

// `</br>` is a common hand-authored stand-in for `<br>` (the HTML parser
// treats them the same in a browser), but it isn't valid markup to hand a
// schema consumer, so it's normalised before the allowlist runs.
export function sanitizeFaqAnswerForSchema(html) {
  return html
    .replace(/<\/br\s*>/gi, '<br>')
    .replace(
      /<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^>]*)>/g,
      (match, closing, tag, attrs) => {
        const tagName = tag.toLowerCase()
        if (!FAQ_ANSWER_ALLOWED_TAGS.includes(tagName)) {
          return ''
        }
        if (tagName === 'a' && !closing) {
          const hrefMatch =
            attrs.match(/href\s*=\s*"([^"]*)"/i) ||
            attrs.match(/href\s*=\s*'([^']*)'/i)
          return hrefMatch ? `<a href="${hrefMatch[1]}">` : '<a>'
        }
        return closing ? `</${tagName}>` : `<${tagName}>`
      }
    )
}
